"use client";

import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import clsx from "clsx";
import {
  Plus,
  Save,
  Sparkles,
  Trash2,
  FileQuestion,
  Search,
  Pencil,
} from "lucide-react";
import { AdminShell } from "@/components/admin-shell";
import { FgCard } from "@/components/fg-card";
import { FgButton } from "@/components/fg-button";
import { FgInput } from "@/components/fg-input";
import { FgToast } from "@/components/fg-toast";
import { FgDialog } from "@/components/fg-dialog";
import { type RubricCriterion } from "@/components/exercise-builder/RubricEditor";
import { MultipleChoiceBuilder, type ChoiceRow } from "@/components/exercise-builder/builders/MultipleChoiceBuilder";
import { FillBlankBuilder } from "@/components/exercise-builder/builders/FillBlankBuilder";
import { MatchingBuilder, type MatchPair } from "@/components/exercise-builder/builders/MatchingBuilder";
import { DictationBuilder } from "@/components/exercise-builder/builders/DictationBuilder";
import { OrderingBuilder } from "@/components/exercise-builder/builders/OrderingBuilder";
import { FreeAnswerBuilder } from "@/components/exercise-builder/builders/FreeAnswerBuilder";
import { FreeWritingBuilder } from "@/components/exercise-builder/builders/FreeWritingBuilder";
import { HelpTip, EXERCISE_TYPE_HELP } from "@/components/exercise-builder/HelpTip";
import { api } from "@/lib/api";

/**
 * Feature flag — set `VITE_EXPERIMENTAL_BUILDER=false` in `.env` to fall
 * back to the legacy JSON editor. Used during the gradual rollout so we
 * can roll back if the new builders misbehave in production.
 */
const USE_EXPERIMENTAL_BUILDER =
  (import.meta.env.VITE_EXPERIMENTAL_BUILDER as string | undefined) !== "false";

/* ──────────────────────────────────────────────────────────────────────────── */

type ExerciseType =
  | "multiple_choice"
  | "fill_blank"
  | "matching"
  | "dictation"
  | "ordering"
  | "audio_mcq"
  | "free_answer"
  | "free_writing";

type Placement = "lesson" | "after_n_lessons" | "mock_exam";

interface Level {
  id: string;
  name: string;
  displayName: string;
}

interface Lesson {
  id: string;
  levelId: string;
  title: string;
  orderIndex: number;
}

interface Exam {
  id: string;
  levelId: string;
  title: string;
  type: "mock_exam" | "after_n_lessons";
}

interface Exercise {
  id: string;
  lessonId: string | null;
  levelId: string | null;
  examId: string | null;
  placement: Placement;
  nLessons: number | null;
  title: string;
  exerciseType: ExerciseType;
  content: Record<string, unknown>;
  maxAnswerLength: number | null;
  rubric: Array<{ criterion: string; weight: number }> | null;
  aiGradingEnabled: boolean;
  orderIndex: number;
}

const TYPES: { id: ExerciseType; label: string; emoji: string; ai: boolean }[] = [
  { id: "multiple_choice", label: "Multiple choice", emoji: "◉", ai: false },
  { id: "fill_blank", label: "Fill in the blank", emoji: "✎", ai: false },
  { id: "matching", label: "Matching pairs", emoji: "↔", ai: false },
  { id: "dictation", label: "Dictation", emoji: "🎧", ai: false },
  { id: "ordering", label: "Word ordering", emoji: "↕", ai: false },
  { id: "audio_mcq", label: "Audio MCQ", emoji: "🔊", ai: false },
  { id: "free_answer", label: "Free answer", emoji: "✍️", ai: true },
  { id: "free_writing", label: "Free writing", emoji: "📝", ai: true },
];



/* ──────────────────────────────────────────────────────────────────────────── */

/**
 * Resolve the CEFR level name from a levelId. Falls back to A1 when the
 * levels list isn't loaded yet or the id doesn't match a known level.
 * The AI builders use this to narrow their prompt templates.
 */
function resolveLevelName(
  levelId: string | undefined,
  levels: Level[] | undefined,
): "A1" | "A2" | "B1" | "B2" | "C1" {
  if (!levelId || !levels) return "A1";
  const found = levels.find((l) => l.id === levelId);
  if (!found) return "A1";
  const code = found.name.toUpperCase();
  if (code === "A1" || code === "A2" || code === "B1" || code === "B2" || code === "C1") {
    return code;
  }
  return "A1";
}

/* ──────────────────────────────────────────────────────────────────────────── */

export function ExercisesPage() {
  const qc = useQueryClient();
  const [scope, setScope] = useState<{
    kind: Placement;
    levelId?: string;
    lessonId?: string;
    examId?: string;
  }>({ kind: "lesson" });
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<Exercise | null>(null);
  const [creating, setCreating] = useState<ExerciseType | null>(null);
  const [deleting, setDeleting] = useState<Exercise | null>(null);

  const levels = useQuery<Level[]>({
    queryKey: ["levels"],
    queryFn: async () => (await api.get("/api/levels")).data,
  });

  const lessons = useQuery<Lesson[]>({
    queryKey: ["lessons", scope.levelId],
    enabled: scope.kind === "lesson" && !!scope.levelId,
    queryFn: async () => (await api.get(`/api/lessons/by-level/${scope.levelId}`)).data,
  });

  const exams = useQuery<Exam[]>({
    queryKey: ["exams", scope.levelId],
    enabled: (scope.kind === "mock_exam" || scope.kind === "after_n_lessons") && !!scope.levelId,
    queryFn: async () => (await api.get(`/api/levels/${scope.levelId}/exams`)).data,
  });

  // Initial scope: lesson-attached, first level, first lesson.
  useEffect(() => {
    if (scope.kind !== "lesson") return;
    if (!scope.levelId && levels.data && levels.data.length > 0) {
      setScope({ kind: "lesson", levelId: levels.data[0].id });
    }
  }, [scope.kind, scope.levelId, levels.data]);

  useEffect(() => {
    if (scope.kind !== "lesson") return;
    if (scope.levelId && !scope.lessonId && lessons.data && lessons.data.length > 0) {
      setScope({ kind: "lesson", levelId: scope.levelId, lessonId: lessons.data[0].id });
    }
  }, [scope, lessons.data]);

  const exercises = useQuery<Exercise[]>({
    queryKey: ["exercises", scope],
    enabled:
      (scope.kind === "lesson" && !!scope.lessonId) ||
      (scope.kind === "mock_exam" && !!scope.examId),
    queryFn: async () => {
      if (scope.kind === "lesson")
        return (await api.get(`/api/exercises/by-lesson/${scope.lessonId}`)).data;
      if (scope.kind === "mock_exam")
        return (await api.get(`/api/exercises/by-exam/${scope.examId}`)).data;
      return [];
    },
  });

  const filtered = useMemo(() => {
    const list = exercises.data ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((e) => e.title.toLowerCase().includes(q));
  }, [exercises.data, search]);

  const create = useMutation({
    mutationFn: async (body: Partial<Exercise>) =>
      (await api.post("/api/exercises", body)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["exercises", scope] });
      FgToast.success("Exercise created");
      setCreating(null);
    },
    onError: (e) =>
      FgToast.error((e as { message?: string }).message ?? "Create failed"),
  });

  const update = useMutation({
    mutationFn: async ({ id, body }: { id: string; body: Partial<Exercise> }) =>
      (await api.patch(`/api/exercises/${id}`, body)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["exercises", scope] });
      FgToast.success("Saved");
      setEditing(null);
    },
    onError: (e) =>
      FgToast.error((e as { message?: string }).message ?? "Save failed"),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/api/exercises/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["exercises", scope] });
      FgToast.success("Exercise deleted");
      setDeleting(null);
    },
    onError: (e) =>
      FgToast.error((e as { message?: string }).message ?? "Delete failed"),
  });

  const hasUsableScope =
    (scope.kind === "lesson" && !!scope.lessonId) ||
    (scope.kind === "mock_exam" && !!scope.examId);

  return (
    <AdminShell>
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-[rgba(255,255,255,0.08)] pb-6 mb-8"
      >
        <div>
          <div className="flex items-center gap-2 mb-1">
            <FileQuestion className="w-5 h-5 text-[#FFD60A]" />
            <span className="text-[11px] font-mono font-bold tracking-widest uppercase text-[#FFD60A]">
              EXERCISES
            </span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-[#FAFAFA]">
            AI Exercises
          </h1>
          <p className="mt-1 text-sm text-[#8E8E93]">
            8 question types · Gemini-graded free answers &amp; writing · consumed by the mobile app
            lesson player
          </p>
        </div>
        <FgButton
          size="sm"
          variant="primary"
          onClick={() => setCreating("multiple_choice")}
          disabled={!hasUsableScope}
          leftIcon={<Plus className="w-4 h-4" />}
        >
          New exercise
        </FgButton>
      </motion.div>

      {/* Scope selector + search — dark filter bar matching SubscriptionsPage */}
      <FgCard className="p-4 mb-6">
        <div className="flex flex-col gap-3 md:flex-row md:items-end">
          <div className="inline-flex shrink-0 items-center gap-1 p-1 rounded-xl bg-[#1C1C1E]/60 border border-[rgba(255,255,255,0.08)] backdrop-blur">
            {(
              [
                { id: "lesson" as const, label: "After lesson" },
                { id: "after_n_lessons" as const, label: "After N lessons" },
                { id: "mock_exam" as const, label: "Mock exam" },
              ] as const
            ).map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() =>
                  setScope({
                    kind: p.id,
                    levelId: levels.data?.[0]?.id,
                  })
                }
                className={clsx(
                  "relative px-3 py-1.5 rounded-lg text-[11px] font-bold transition-colors",
                  scope.kind === p.id
                    ? "text-[#0A0A0A]"
                    : "text-[#8E8E93] hover:text-[#FAFAFA]",
                )}
              >
                {scope.kind === p.id && (
                  <motion.div
                    layoutId="exercises-scope-tab"
                    className="absolute inset-0 bg-[#FFD60A] rounded-lg"
                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                  />
                )}
                <span className="relative z-10">{p.label}</span>
              </button>
            ))}
          </div>

          <div className="grid flex-1 grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#8E8E93] mb-1.5">
                Level
              </label>
              <select
                value={scope.levelId ?? ""}
                onChange={(e) =>
                  setScope({ kind: scope.kind, levelId: e.target.value })
                }
                className="h-9 w-full rounded-lg border border-[rgba(255,255,255,0.1)] bg-[#1C1C1E] px-3 text-sm text-[#FAFAFA] focus:outline-none focus:border-[#FFD60A]"
              >
                <option value="">— select —</option>
                {levels.data?.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name} · {l.displayName}
                  </option>
                ))}
              </select>
            </div>

            {scope.kind === "lesson" && (
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#8E8E93] mb-1.5">
                  Lesson
                </label>
                <select
                  value={scope.lessonId ?? ""}
                  onChange={(e) =>
                    setScope({
                      kind: "lesson",
                      levelId: scope.levelId,
                      lessonId: e.target.value,
                    })
                  }
                  className="h-9 w-full rounded-lg border border-[rgba(255,255,255,0.1)] bg-[#1C1C1E] px-3 text-sm text-[#FAFAFA] focus:outline-none focus:border-[#FFD60A]"
                  disabled={!scope.levelId}
                >
                  <option value="">— select —</option>
                  {lessons.data?.map((l) => (
                    <option key={l.id} value={l.id}>
                      #{l.orderIndex} · {l.title}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {scope.kind === "mock_exam" && (
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#8E8E93] mb-1.5">
                  Exam
                </label>
                <select
                  value={scope.examId ?? ""}
                  onChange={(e) =>
                    setScope({
                      kind: "mock_exam",
                      levelId: scope.levelId,
                      examId: e.target.value,
                    })
                  }
                  className="h-9 w-full rounded-lg border border-[rgba(255,255,255,0.1)] bg-[#1C1C1E] px-3 text-sm text-[#FAFAFA] focus:outline-none focus:border-[#FFD60A]"
                  disabled={!scope.levelId}
                >
                  <option value="">— select —</option>
                  {exams.data?.map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.title}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#8E8E93] mb-1.5">
                Filter
              </label>
              <div className="relative">
                <Search
                  size={13}
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#8E8E93]"
                />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Filter by title…"
                  className="w-full h-9 rounded-lg border border-[rgba(255,255,255,0.1)] bg-[#1C1C1E] pl-7 pr-3 text-sm text-[#FAFAFA] placeholder:text-[#8E8E93] focus:outline-none focus:border-[#FFD60A]"
                />
              </div>
            </div>
          </div>
        </div>
      </FgCard>

      {/* List */}
      {!hasUsableScope ? (
        <FgCard className="p-10 text-center">
          <FileQuestion className="w-10 h-10 mx-auto text-[#8E8E93] mb-3" />
          <p className="text-sm text-[#FAFAFA] font-semibold">Pick a scope</p>
          <p className="mt-1 text-xs text-[#8E8E93]">
            Select a level + lesson (or level + exam) to see its exercises.
          </p>
        </FgCard>
      ) : exercises.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-20 rounded-xl bg-white/5 fg-skeleton" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <FgCard className="p-10 text-center">
          <Plus className="w-10 h-10 mx-auto text-[#8E8E93] mb-3" />
          <p className="text-sm text-[#FAFAFA] font-semibold">No exercises yet</p>
          <p className="mt-1 text-xs text-[#8E8E93]">
            Click <em>New exercise</em> to add the first one — pick a type and tweak the
            content.
          </p>
        </FgCard>
      ) : (
        <div className="space-y-2">
          <AnimatePresence mode="popLayout">
            {filtered.map((ex, idx) => (
              <motion.div
                key={ex.id}
                layout
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96, transition: { duration: 0.15 } }}
                transition={{ duration: 0.3, delay: idx * 0.04 }}
              >
                <ExerciseRow
                  exercise={ex}
                  onEdit={() => setEditing(ex)}
                  onDelete={() => setDeleting(ex)}
                />
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}

      {/* Type picker — pinned at the bottom like the tier create button */}
      {hasUsableScope && (
        <FgCard className="mt-6 p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-base font-bold text-[#FAFAFA]">Add a new exercise</h2>
              <p className="mt-1 text-xs text-[#8E8E93]">
                Pick a question type — a JSON template is seeded with sensible defaults.
                Tweak inline and Save.
              </p>
            </div>
            <span className="text-[10px] font-mono uppercase tracking-widest text-[#8E8E93]">
              8 types
            </span>
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-8">
            {TYPES.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setCreating(t.id)}
                className={clsx(
                  "flex flex-col items-center justify-center gap-1 rounded-lg border border-[rgba(255,255,255,0.08)] bg-[#141414] p-3 text-center transition-colors hover:border-[#FFD60A]/40 hover:bg-[#1C1C1E]",
                )}
              >
                <span className="text-lg">{t.emoji}</span>
                <span className="text-[11px] font-bold text-[#FAFAFA]">{t.label}</span>
                <span className="flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider text-[#8E8E93]">
                  {t.ai ? (
                    <>
                      <Sparkles size={9} /> AI graded
                    </>
                  ) : (
                    <>deterministic</>
                  )}
                </span>
              </button>
            ))}
          </div>
        </FgCard>
      )}

      {creating && (
        <ExerciseEditor
          mode="create"
          exerciseType={creating}
          scope={scope}
          levelName={resolveLevelName(scope.levelId, levels.data)}
          onClose={() => setCreating(null)}
          onSubmit={(body) => create.mutate(body)}
          busy={create.isPending}
        />
      )}
      {editing && (
        <ExerciseEditor
          mode="edit"
          exercise={editing}
          scope={scope}
          levelName={resolveLevelName(scope.levelId, levels.data)}
          onClose={() => setEditing(null)}
          onSubmit={(body) => update.mutate({ id: editing.id, body })}
          busy={update.isPending}
        />
      )}
      {deleting && (
        <FgDialog
          open
          size="sm"
          tone="danger"
          title="Delete exercise?"
          description={`"${deleting.title}" will be removed permanently. Learners in mid-session will see an error.`}
          onClose={() => setDeleting(null)}
          footer={
            <>
              <FgButton variant="ghost" onClick={() => setDeleting(null)}>
                Cancel
              </FgButton>
              <FgButton
                variant="destructive"
                onClick={() => remove.mutate(deleting.id)}
                loading={remove.isPending}
                leftIcon={<Trash2 size={14} />}
              >
                Delete
              </FgButton>
            </>
          }
        />
      )}
    </AdminShell>
  );
}

/* ──────────────────────────────────────────────────────────────────────────── */

function ExerciseRow({
  exercise,
  onEdit,
  onDelete,
}: {
  exercise: Exercise;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const typeMeta = TYPES.find((t) => t.id === exercise.exerciseType);
  return (
    <FgCard className="p-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="rounded bg-[#FFD60A] px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wider text-[#0A0A0A]">
              {typeMeta?.label ?? exercise.exerciseType}
            </span>
            <span className="text-base font-bold text-[#FAFAFA]">
              #{exercise.orderIndex} · {exercise.title}
            </span>
            {exercise.aiGradingEnabled && (
              <span className="inline-flex items-center gap-1 rounded-full bg-[#FFD60A]/15 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wider text-[#FFD60A] border border-[#FFD60A]/30">
                <Sparkles size={10} /> AI graded
              </span>
            )}
            {exercise.rubric && exercise.rubric.length > 0 && (
              <span className="text-[10px] font-mono text-[#8E8E93]">
                rubric · {exercise.rubric.length} criteria
              </span>
            )}
          </div>
          <p className="text-[11px] text-[#8E8E93] font-mono">
            {exercise.exerciseType} · order {exercise.orderIndex}
            {exercise.maxAnswerLength ? ` · max ${exercise.maxAnswerLength} chars` : ""}
          </p>
        </div>
        <div className="flex gap-2">
          <FgButton
            size="sm"
            variant="outline"
            onClick={onEdit}
            leftIcon={<Pencil size={12} />}
          >
            Edit
          </FgButton>
          <FgButton
            size="sm"
            variant="ghost"
            onClick={onDelete}
            leftIcon={<Trash2 size={14} />}
          >
            Delete
          </FgButton>
        </div>
      </div>
    </FgCard>
  );
}

/* ──────────────────────────────────────────────────────────────────────────── */

interface EditorProps {
  mode: "create" | "edit";
  scope: { kind: Placement; levelId?: string; lessonId?: string; examId?: string };
  /** CEFR name of the current level (e.g. "A1") — drives AI rubric + choice
   *  generation. Falls back to A1 when no level is selected. */
  levelName: "A1" | "A2" | "B1" | "B2" | "C1";
  busy: boolean;
  onClose: () => void;
  onSubmit: (body: Partial<Exercise>) => void;
  exerciseType?: ExerciseType;
  exercise?: Exercise;
}

function ExerciseEditor({
  mode,
  exerciseType,
  exercise,
  scope,
  levelName,
  busy,
  onClose,
  onSubmit,
}: EditorProps) {
  const type: ExerciseType = exercise?.exerciseType ?? exerciseType ?? "multiple_choice";

  // ── Common header state ───────────────────────────────────────────────
  const [title, setTitle] = useState(exercise?.title ?? `New ${typeMetaFor(type).label}`);
  const [orderIndex, setOrderIndex] = useState(String(exercise?.orderIndex ?? 1));
  const [nLessons, setNLessons] = useState(String(exercise?.nLessons ?? 3));
  const [aiEnabled, setAiEnabled] = useState(
    exercise?.aiGradingEnabled ?? TYPES.find((t) => t.id === type)?.ai ?? false,
  );

  // ── Per-builder state ─────────────────────────────────────────────────
  // MCQ + audio_mcq
  const [mcqPrompt, setMcqPrompt] = useState(
    (exercise?.content?.prompt as string) ?? "",
  );
  const [mcqPromptAudio, setMcqPromptAudio] = useState(
    (exercise?.content?.promptAudioUrl as string) ?? "",
  );
  const [mcqChoices, setMcqChoices] = useState<ChoiceRow[]>(
    choicesFromContent(exercise?.content, type),
  );
  const [mcqExplanation, setMcqExplanation] = useState(
    (exercise?.content?.explanation as string) ?? "",
  );

  // Fill-blank
  const [fbSentence, setFbSentence] = useState(
    (exercise?.content?.prompt as string) ?? "",
  );
  const [fbVariants, setFbVariants] = useState<string[]>(
    Array.isArray(exercise?.content?.acceptableAnswers)
      ? (exercise?.content?.acceptableAnswers as string[])
      : [],
  );
  const [fbHints, setFbHints] = useState<string[]>(
    Array.isArray(exercise?.content?.hints) ? (exercise?.content?.hints as string[]) : [],
  );
  const [fbCaseSensitive, setFbCaseSensitive] = useState(
    Boolean(exercise?.content?.caseSensitive),
  );
  const [fbWhitespaceTolerant, setFbWhitespaceTolerant] = useState(
    exercise?.content?.whitespaceTolerant === undefined
      ? true
      : Boolean(exercise?.content?.whitespaceTolerant),
  );
  const [fbExplanation, setFbExplanation] = useState(
    (exercise?.content?.explanation as string) ?? "",
  );

  // Matching
  const [matchPrompt, setMatchPrompt] = useState(
    (exercise?.content?.prompt as string) ?? "",
  );
  const [matchPairs, setMatchPairs] = useState<MatchPair[]>(
    matchPairsFromContent(exercise?.content),
  );
  const [matchShuffle, setMatchShuffle] = useState(
    exercise?.content?.shuffleOnRender !== false,
  );

  // Dictation
  const [dictPrompt, setDictPrompt] = useState(
    (exercise?.content?.prompt as string) ?? "",
  );
  const [dictAudio, setDictAudio] = useState(
    (exercise?.content?.audioUrl as string) ?? "",
  );
  const [dictAnswer, setDictAnswer] = useState(
    (exercise?.content?.answer as string) ?? "",
  );
  const [dictListenCount, setDictListenCount] = useState(
    Number(exercise?.content?.listenCount ?? 3),
  );

  // Ordering
  const [orderPrompt, setOrderPrompt] = useState(
    (exercise?.content?.prompt as string) ?? "",
  );
  const [orderCanonical, setOrderCanonical] = useState(
    (exercise?.content?.canonical as string) ?? "",
  );
  const [orderBank, setOrderBank] = useState<string[]>(
    Array.isArray(exercise?.content?.tokens) ? (exercise?.content?.tokens as string[]) : [],
  );
  const [orderRail, setOrderRail] = useState<string[]>(
    Array.isArray(exercise?.content?.correctOrder) ? (exercise?.content?.correctOrder as string[]) : [],
  );
  const [orderPunct, setOrderPunct] = useState(
    Boolean(exercise?.content?.tokenizePunctuation),
  );

  // Free answer
  const [faPromptKind, setFaPromptKind] = useState<"text" | "audio">(
    (exercise?.content?.prompt as { kind?: string } | undefined)?.kind === "audio"
      ? "audio"
      : "text",
  );
  const [faPromptText, setFaPromptText] = useState<string>(
    typeof exercise?.content?.prompt === "string"
      ? exercise.content.prompt
      : ((exercise?.content?.prompt as { text?: string } | undefined)?.text ?? ""),
  );
  const [faPromptAudio, setFaPromptAudio] = useState<string>(
    ((exercise?.content?.prompt as { audioUrl?: string } | undefined)?.audioUrl ?? ""),
  );
  const [faReference, setFaReference] = useState(
    (exercise?.content?.referenceAnswer as string) ?? "",
  );
  const [faMaxLength, setFaMaxLength] = useState(
    Number(exercise?.maxAnswerLength ?? 200),
  );
  const [faDifficulty, setFaDifficulty] = useState(
    Number(exercise?.content?.difficulty ?? 2),
  );
  const [faRubric, setFaRubric] = useState<RubricCriterion[]>(
    (exercise?.rubric as RubricCriterion[]) ?? defaultRubric("free_answer"),
  );

  // Free writing
  const [fwPrompt, setFwPrompt] = useState(
    (exercise?.content?.prompt as string) ?? "",
  );
  const [fwMinWords, setFwMinWords] = useState(
    Number(exercise?.content?.minWords ?? 80),
  );
  const [fwMaxWords, setFwMaxWords] = useState(
    Number(exercise?.content?.maxWords ?? 120),
  );
  const [fwMaxLength, setFwMaxLength] = useState(
    Number(exercise?.maxAnswerLength ?? 1000),
  );
  const [fwDifficulty, setFwDifficulty] = useState(
    Number(exercise?.content?.difficulty ?? 3),
  );
  const [fwRubric, setFwRubric] = useState<RubricCriterion[]>(
    (exercise?.rubric as RubricCriterion[]) ?? defaultRubric("free_writing"),
  );

  // `rubric` is read by `buildBody()` (and by no JSX). The setter stays here
  // as the entry-point the per-type builder writes to via the dispatcher.
  // `rubric` is read by `buildBody()` below. The setter stays here so
// the per-type builder writes to the right slice (fa vs fw) when
// `aiGradingEnabled` is toggled.
const rubric: RubricCriterion[] =
  type === "free_answer" ? faRubric : type === "free_writing" ? fwRubric : [];
const _setRubric = (next: RubricCriterion[]) => {
  if (type === "free_answer") setFaRubric(next);
  else if (type === "free_writing") setFwRubric(next);
};
void _setRubric; // intentionally retained for future per-type dispatch

  // ── Serialize the per-builder state → the JSON content shape ──────────
  const content = useMemo<Record<string, unknown>>(() => {
    switch (type) {
      case "multiple_choice":
        return mcqContentSerialize(mcqPrompt, mcqChoices, mcqExplanation);
      case "audio_mcq":
        return audioMcqContentSerialize(
          mcqPrompt,
          mcqPromptAudio,
          mcqChoices,
          mcqExplanation,
        );
      case "fill_blank":
        return fillBlankContentSerialize(
          fbSentence,
          fbVariants,
          fbHints,
          fbCaseSensitive,
          fbWhitespaceTolerant,
          fbExplanation,
        );
      case "matching":
        return matchingContentSerialize(matchPrompt, matchPairs, matchShuffle);
      case "dictation":
        return dictationContentSerialize(dictPrompt, dictAudio, dictAnswer, dictListenCount);
      case "ordering":
        return orderingContentSerialize(
          orderPrompt,
          orderCanonical,
          orderBank,
          orderRail,
          orderPunct,
        );
      case "free_answer":
        return freeAnswerContentSerialize(
          faPromptKind,
          faPromptText,
          faPromptAudio,
          faReference,
          faDifficulty,
        );
      case "free_writing":
        return freeWritingContentSerialize(
          fwPrompt,
          fwMinWords,
          fwMaxWords,
          fwDifficulty,
        );
      default:
        return {};
    }
  }, [
    type,
    mcqPrompt,
    mcqPromptAudio,
    mcqChoices,
    mcqExplanation,
    fbSentence,
    fbVariants,
    fbHints,
    fbCaseSensitive,
    fbWhitespaceTolerant,
    fbExplanation,
    matchPrompt,
    matchPairs,
    matchShuffle,
    dictPrompt,
    dictAudio,
    dictAnswer,
    dictListenCount,
    orderPrompt,
    orderCanonical,
    orderBank,
    orderRail,
    orderPunct,
    faPromptKind,
    faPromptText,
    faPromptAudio,
    faReference,
    faDifficulty,
    fwPrompt,
    fwMinWords,
    fwMaxWords,
    fwDifficulty,
  ]);

  const maxAnswerLength = (() => {
    switch (type) {
      case "free_answer":
        return faMaxLength;
      case "free_writing":
        return fwMaxLength;
      default:
        return null;
    }
  })();

  function buildBody(): Partial<Exercise> {
    const body: Partial<Exercise> = {
      title,
      exerciseType: type,
      orderIndex: Number(orderIndex) || 1,
      content,
      maxAnswerLength: maxAnswerLength && maxAnswerLength > 0 ? maxAnswerLength : null,
      rubric: rubric.length > 0 ? rubric : null,
      aiGradingEnabled: aiEnabled,
      placement: scope.kind,
    };
    if (scope.kind === "lesson") body.lessonId = scope.lessonId ?? null;
    if (scope.kind === "mock_exam") body.examId = scope.examId ?? null;
    if (scope.kind === "after_n_lessons") {
      body.levelId = scope.levelId ?? null;
      body.nLessons = Number(nLessons) || 1;
    } else if (scope.levelId) {
      body.levelId = scope.levelId;
    }
    return body;
  }

  const isAiGraded = TYPES.find((t) => t.id === type)?.ai;

  return (
    <FgDialog
      open
      size="lg"
      tone={isAiGraded ? "warning" : "default"}
      confirmOnClose
      confirmMessage="Discard unsaved changes to this exercise?"
      title={
        mode === "create"
          ? `New · ${typeMetaFor(type).label}`
          : `Edit · ${exercise?.title ?? "exercise"}`
      }
      description={
        isAiGraded
          ? "This exercise is graded by Gemini. Configure the rubric + length below."
          : "Drag to reorder · click ✨ to let Gemini draft content for you."
      }
      onClose={onClose}
      footer={
        <>
          <FgButton variant="ghost" onClick={onClose}>
            Cancel
          </FgButton>
          <FgButton
            variant="primary"
            onClick={() => onSubmit(buildBody())}
            loading={busy}
            leftIcon={<Save size={14} />}
          >
            {mode === "create" ? "Create" : "Save"}
          </FgButton>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(buildBody());
        }}
      >
        <div className="flex items-center gap-2">
          <p className="text-[10px] font-mono uppercase tracking-widest text-[#8E8E93]">
            Type guide
          </p>
          <HelpTip {...(EXERCISE_TYPE_HELP[type] ?? { title: type, description: "" })} />
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <FgInput
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />
          <FgInput
            label="Order index"
            type="number"
            value={orderIndex}
            onChange={(e) => setOrderIndex(e.target.value)}
          />
        </div>

        {scope.kind === "after_n_lessons" && (
          <FgInput
            label="Unlocks after N lessons in this level"
            type="number"
            value={nLessons}
            onChange={(e) => setNLessons(e.target.value)}
          />
        )}

        {/* ── Builder dispatcher ───────────────────────────────────────── */}
        {USE_EXPERIMENTAL_BUILDER ? (
          <BuilderDispatcher
            type={type}
            level={levelName}
            mcq={{
              prompt: mcqPrompt,
              onPromptChange: setMcqPrompt,
              promptAudioUrl: mcqPromptAudio,
              onPromptAudioUrlChange: setMcqPromptAudio,
              choices: mcqChoices,
              onChoicesChange: setMcqChoices,
              explanation: mcqExplanation,
              onExplanationChange: setMcqExplanation,
              variant: type === "audio_mcq" ? "audio_mcq" : "multiple_choice",
              level: levelName,
            }}
            fillBlank={{
              sentence: fbSentence,
              onSentenceChange: setFbSentence,
              variants: fbVariants,
              onVariantsChange: setFbVariants,
              hints: fbHints,
              onHintsChange: setFbHints,
              caseSensitive: fbCaseSensitive,
              onCaseSensitiveChange: setFbCaseSensitive,
              whitespaceTolerant: fbWhitespaceTolerant,
              onWhitespaceTolerantChange: setFbWhitespaceTolerant,
              explanation: fbExplanation,
              onExplanationChange: setFbExplanation,
              level: levelName,
            }}
            matching={{
              prompt: matchPrompt,
              onPromptChange: setMatchPrompt,
              pairs: matchPairs,
              onPairsChange: setMatchPairs,
              shuffleOnRender: matchShuffle,
              onShuffleChange: setMatchShuffle,
              level: levelName,
            }}
            dictation={{
              prompt: dictPrompt,
              onPromptChange: setDictPrompt,
              audioUrl: dictAudio,
              onAudioUrlChange: setDictAudio,
              answer: dictAnswer,
              onAnswerChange: setDictAnswer,
              listenCount: dictListenCount,
              onListenCountChange: setDictListenCount,
              level: levelName,
            }}
            ordering={{
              prompt: orderPrompt,
              onPromptChange: setOrderPrompt,
              canonicalSentence: orderCanonical,
              onCanonicalSentenceChange: setOrderCanonical,
              bank: orderBank,
              onBankChange: setOrderBank,
              rail: orderRail,
              onRailChange: setOrderRail,
              tokenizePunctuation: orderPunct,
              onTokenizePunctuationChange: setOrderPunct,
              level: levelName,
            }}
            freeAnswer={{
              promptKind: faPromptKind,
              onPromptKindChange: setFaPromptKind,
              promptText: faPromptText,
              onPromptTextChange: setFaPromptText,
              promptAudioUrl: faPromptAudio,
              onPromptAudioUrlChange: setFaPromptAudio,
              referenceAnswer: faReference,
              onReferenceAnswerChange: setFaReference,
              maxLength: faMaxLength,
              onMaxLengthChange: setFaMaxLength,
              difficulty: faDifficulty,
              onDifficultyChange: setFaDifficulty,
              aiGradingEnabled: aiEnabled,
              onAiGradingEnabledChange: setAiEnabled,
              rubric: faRubric,
              onRubricChange: setFaRubric,
              level: levelName,
            }}
            freeWriting={{
              prompt: fwPrompt,
              onPromptChange: setFwPrompt,
              minWords: fwMinWords,
              onMinWordsChange: setFwMinWords,
              maxWords: fwMaxWords,
              onMaxWordsChange: setFwMaxWords,
              maxLength: fwMaxLength,
              onMaxLengthChange: setFwMaxLength,
              difficulty: fwDifficulty,
              onDifficultyChange: setFwDifficulty,
              aiGradingEnabled: aiEnabled,
              onAiGradingEnabledChange: setAiEnabled,
              rubric: fwRubric,
              onRubricChange: setFwRubric,
              level: levelName,
            }}
          />
        ) : (
          <LegacyJsonFallback type={type} content={content} onChange={() => undefined} />
        )}
      </form>
    </FgDialog>
  );
}

function typeMetaFor(type: ExerciseType) {
  return TYPES.find((t) => t.id === type) ?? TYPES[0];
}

/* ────────────────────────────────────────────────────────────────────────────
 * Builder dispatcher + content serialisers.
 *
 * Each builder owns the visual state (lists, toggles, inputs) and we
 * serialise that state into the JSON `content` shape the backend expects.
 * The shapes match what the mobile app + Phase 4b grading pipeline already
 * understand, so existing exercises keep rendering correctly after edits.
 * ──────────────────────────────────────────────────────────────────────────── */

type CefrLevel = "A1" | "A2" | "B1" | "B2" | "C1";

interface BuilderDispatcherProps {
  type: ExerciseType;
  level: CefrLevel;
  mcq: Parameters<typeof MultipleChoiceBuilder>[0];
  fillBlank: Parameters<typeof FillBlankBuilder>[0];
  matching: Parameters<typeof MatchingBuilder>[0];
  dictation: Parameters<typeof DictationBuilder>[0];
  ordering: Parameters<typeof OrderingBuilder>[0];
  freeAnswer: Parameters<typeof FreeAnswerBuilder>[0];
  freeWriting: Parameters<typeof FreeWritingBuilder>[0];
}

function BuilderDispatcher(props: BuilderDispatcherProps) {
  switch (props.type) {
    case "multiple_choice":
      return (
        <MultipleChoiceBuilder
          variant="multiple_choice"
          prompt={props.mcq.prompt}
          onPromptChange={props.mcq.onPromptChange}
          promptAudioUrl={props.mcq.promptAudioUrl}
          onPromptAudioUrlChange={props.mcq.onPromptAudioUrlChange}
          choices={props.mcq.choices}
          onChoicesChange={props.mcq.onChoicesChange}
          explanation={props.mcq.explanation}
          onExplanationChange={props.mcq.onExplanationChange}
          level={props.level}
        />
      );
    case "audio_mcq":
      return (
        <MultipleChoiceBuilder
          variant="audio_mcq"
          prompt={props.mcq.prompt}
          onPromptChange={props.mcq.onPromptChange}
          promptAudioUrl={props.mcq.promptAudioUrl}
          onPromptAudioUrlChange={props.mcq.onPromptAudioUrlChange}
          choices={props.mcq.choices}
          onChoicesChange={props.mcq.onChoicesChange}
          explanation={props.mcq.explanation}
          onExplanationChange={props.mcq.onExplanationChange}
          level={props.level}
        />
      );
    case "fill_blank":
      return <FillBlankBuilder {...props.fillBlank} level={props.level} />;
    case "matching":
      return <MatchingBuilder {...props.matching} level={props.level} />;
    case "dictation":
      return <DictationBuilder {...props.dictation} level={props.level} />;
    case "ordering":
      return <OrderingBuilder {...props.ordering} level={props.level} />;
    case "free_answer":
      return <FreeAnswerBuilder {...props.freeAnswer} level={props.level} />;
    case "free_writing":
      return <FreeWritingBuilder {...props.freeWriting} level={props.level} />;
    default:
      return null;
  }
}

/* ── Helpers: parse legacy content back into builder state ─────────────── */

function choicesFromContent(
  content: Record<string, unknown> | undefined,
  type: ExerciseType,
): ChoiceRow[] {
  if (!content) {
    return type === "audio_mcq"
      ? []
      : [
          { id: crypto.randomUUID(), kind: "text", value: "", isCorrect: false },
          { id: crypto.randomUUID(), kind: "text", value: "", isCorrect: false },
        ];
  }
  if (Array.isArray(content.options)) {
    // Legacy MCQ: options[] + correctIndex
    const correctIndex = Number(content.correctIndex ?? -1);
    return (content.options as string[]).map((value, i) => ({
      id: crypto.randomUUID(),
      kind: "text" as const,
      value,
      isCorrect: i === correctIndex,
    }));
  }
  if (Array.isArray(content.choices)) {
    // New MCQ + audio_mcq
    return (content.choices as Array<{ kind?: string; value?: string; isCorrect?: boolean }>).map(
      (c) => ({
        id: crypto.randomUUID(),
        kind: (c.kind as ChoiceRow["kind"]) ?? "text",
        value: c.value ?? "",
        isCorrect: Boolean(c.isCorrect),
      }),
    );
  }
  return [];
}

function matchPairsFromContent(content: Record<string, unknown> | undefined): MatchPair[] {
  if (!content) return [];
  if (Array.isArray(content.pairs)) {
    return (content.pairs as Array<{ de?: string; en?: string }>).map((p) => ({
      id: crypto.randomUUID(),
      de: p.de ?? "",
      en: p.en ?? "",
    }));
  }
  return [];
}

/* ── Serialisers: builder state → JSON content shape ──────────────────── */

function mcqContentSerialize(
  prompt: string,
  choices: ChoiceRow[],
  explanation: string,
): Record<string, unknown> {
  const textChoices = choices.filter((c) => c.kind === "text" && c.value.trim());
  const correctIdx = choices.findIndex((c) => c.isCorrect);
  return {
    prompt,
    options: textChoices.map((c) => c.value),
    correctIndex: textChoices.findIndex((_, i) => {
      const originalIdx = choices.findIndex((c) => c.id === textChoices[i].id);
      return originalIdx === correctIdx;
    }),
    // Also include the heterogeneous-shape `choices` for audio_mcq + the new mobile renderer.
    choices: choices
      .filter((c) => c.value.trim())
      .map((c) => ({
        kind: c.kind,
        value: c.value,
        isCorrect: c.isCorrect,
      })),
    explanation: explanation || undefined,
  };
}

function audioMcqContentSerialize(
  prompt: string,
  promptAudioUrl: string,
  choices: ChoiceRow[],
  explanation: string,
): Record<string, unknown> {
  const base = mcqContentSerialize(prompt, choices, explanation);
  return { ...base, prompt: { kind: "audio", audioUrl: promptAudioUrl }, promptAudioUrl };
}

function fillBlankContentSerialize(
  sentence: string,
  variants: string[],
  hints: string[],
  caseSensitive: boolean,
  whitespaceTolerant: boolean,
  explanation: string,
): Record<string, unknown> {
  const cleanVariants = variants.map((v) => v.trim()).filter(Boolean);
  return {
    prompt: sentence,
    answer: cleanVariants[0] ?? "",
    acceptableAnswers: cleanVariants,
    hints: hints.filter((h) => h.trim()),
    caseSensitive,
    whitespaceTolerant,
    explanation: explanation || undefined,
  };
}

function matchingContentSerialize(
  prompt: string,
  pairs: MatchPair[],
  shuffleOnRender: boolean,
): Record<string, unknown> {
  return {
    prompt,
    pairs: pairs
      .filter((p) => p.de.trim() || p.en.trim())
      .map((p) => ({ de: p.de.trim(), en: p.en.trim() })),
    shuffleOnRender,
  };
}

function dictationContentSerialize(
  prompt: string,
  audioUrl: string,
  answer: string,
  listenCount: number,
): Record<string, unknown> {
  return {
    prompt,
    audioUrl,
    answer,
    listenCount,
  };
}

function orderingContentSerialize(
  prompt: string,
  canonical: string,
  bank: string[],
  rail: string[],
  tokenizePunctuation: boolean,
): Record<string, unknown> {
  // The mobile app uses `correctOrder` as the canonical sequence. Derive it
  // from the rail if the admin arranged the tokens, else from the canonical
  // sentence + bank.
  let correctOrder: string[];
  if (rail.length > 0) {
    correctOrder = rail;
  } else if (canonical.trim()) {
    correctOrder = canonical.trim().split(/\s+/);
  } else {
    correctOrder = bank;
  }
  return {
    prompt,
    canonical: canonical || undefined,
    tokens: bank,
    correctOrder,
    tokenizePunctuation,
  };
}

function freeAnswerContentSerialize(
  promptKind: "text" | "audio",
  promptText: string,
  promptAudioUrl: string,
  referenceAnswer: string,
  difficulty: number,
): Record<string, unknown> {
  const prompt =
    promptKind === "audio"
      ? { kind: "audio", audioUrl: promptAudioUrl, text: promptText || undefined }
      : { kind: "text", text: promptText, audioUrl: undefined };
  return {
    prompt,
    referenceAnswer,
    difficulty,
  };
}

function freeWritingContentSerialize(
  prompt: string,
  minWords: number,
  maxWords: number,
  difficulty: number,
): Record<string, unknown> {
  return { prompt, minWords, maxWords, difficulty };
}

/* ── Defaults ────────────────────────────────────────────────────────── */

function defaultRubric(kind: "free_answer" | "free_writing"): RubricCriterion[] {
  if (kind === "free_writing") {
    return [
      { criterion: "Grammar", weight: 30 },
      { criterion: "Vocabulary", weight: 30 },
      { criterion: "Task completion", weight: 40 },
    ];
  }
  return [
    { criterion: "Accuracy", weight: 60 },
    { criterion: "Grammar", weight: 40 },
  ];
}

/* ── Legacy JSON fallback (only when VITE_EXPERIMENTAL_BUILDER=false) ─── */

function LegacyJsonFallback({
  type,
  content,
  onChange,
}: {
  type: ExerciseType;
  content: Record<string, unknown>;
  onChange: (v: Record<string, unknown>) => void;
}) {
  const [text, setText] = useState(JSON.stringify(content, null, 2));
  const [err, setErr] = useState<string | null>(null);

  return (
    <div>
      <p className="text-[11px] font-mono uppercase tracking-wider text-[#8E8E93] mb-1.5">
        Legacy JSON editor · {type}
      </p>
      <textarea
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          try {
            onChange(JSON.parse(e.target.value));
            setErr(null);
          } catch (parseErr) {
            setErr((parseErr as Error).message);
          }
        }}
        spellCheck={false}
        className="h-56 w-full rounded-lg border border-[rgba(255,255,255,0.08)] bg-[#141414] p-3 font-mono text-xs text-[#FAFAFA] focus:outline-none focus:border-[#FFD60A]"
      />
      {err ? (
        <p className="mt-1 text-xs font-mono text-[#FF453A]">⚠ {err}</p>
      ) : (
        <p className="mt-1 text-xs font-mono text-[#34C759]">✓ valid JSON</p>
      )}
    </div>
  );
}

